import { httpsCallable } from 'firebase/functions';
import { getClientFunctions } from '@/lib/firebase-client';

/**
 * Notification Service
 * 
 * Provides methods to interact with Firebase Functions for notification management.
 * Uses Firebase callable functions for marking notifications as read/deleted.
 */

interface MarkNotificationReadResponse {
  success: boolean;
  alreadyRead?: boolean;
}

interface DeleteNotificationResponse {
  success: boolean;
  alreadyDeleted?: boolean;
}

interface MarkAllNotificationsReadResponse {
  success: boolean;
  markedCount: number;
}

/**
 * Marks a single notification as read
 * @param notificationId - The ID of the notification to mark as read
 * @returns Promise<boolean> - True if successful, false otherwise
 */
export async function markNotificationAsRead(notificationId: string): Promise<boolean> {
  try {
    const functions = getClientFunctions();
    if (!functions) {
      console.error('[NotificationService] Functions not available');
      return false;
    }

    const markReadFn = httpsCallable<
      { notificationId: string },
      MarkNotificationReadResponse
    >(functions, 'markNotificationRead');
    
    const result = await markReadFn({ notificationId });
    return result.data.success;
  } catch (error) {
    console.error('[NotificationService] Error marking notification as read:', error);
    return false;
  }
}

/**
 * Marks all notifications as read for the current user
 * @returns Promise<number> - Number of notifications marked as read, or -1 on error
 */
export async function markAllNotificationsAsRead(): Promise<number> {
  try {
    const functions = getClientFunctions();
    if (!functions) {
      console.error('[NotificationService] Functions not available');
      return -1;
    }

    const markAllReadFn = httpsCallable<void, MarkAllNotificationsReadResponse>(
      functions,
      'markAllNotificationsRead'
    );
    
    const result = await markAllReadFn();
    return result.data.markedCount;
  } catch (error) {
    console.error('[NotificationService] Error marking all notifications as read:', error);
    return -1;
  }
}

/**
 * Deletes a notification
 * @param notificationId - The ID of the notification to delete
 * @returns Promise<boolean> - True if successful, false otherwise
 */
export async function deleteNotification(notificationId: string): Promise<boolean> {
  try {
    const functions = getClientFunctions();
    if (!functions) {
      console.error('[NotificationService] Functions not available');
      return false;
    }

    const deleteFn = httpsCallable<
      { notificationId: string },
      DeleteNotificationResponse
    >(functions, 'deleteNotification');
    
    const result = await deleteFn({ notificationId });
    return result.data.success;
  } catch (error) {
    console.error('[NotificationService] Error deleting notification:', error);
    return false;
  }
}
